import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { GpsUserPermissions } from '@/types/gps';
import { toast } from 'sonner';

export function useGpsUserPermissions(companyId?: string) {
  const queryClient = useQueryClient();

  // Fetch all GPS user permissions for the company (admin only)
  const { data: permissions, isLoading } = useQuery({
    queryKey: ['gps-user-permissions', companyId],
    queryFn: async (): Promise<GpsUserPermissions[]> => {
      if (!companyId) return [];

      const { data, error } = await supabase
        .from('gps_user_permissions' as any)
        .select('*')
        .eq('company_id', companyId);

      if (error) {
        console.error('Error fetching GPS permissions:', error);
        return [];
      }

      return (data || []) as unknown as GpsUserPermissions[];
    },
    enabled: !!companyId,
  });

  // Get current user GPS permissions (with defaults if not set)
  const { data: myPermissions } = useQuery({
    queryKey: ['gps-my-permissions', companyId],
    queryFn: async (): Promise<GpsUserPermissions | null> => {
      if (!companyId) return null;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Try to fetch existing permissions
      const { data: existingPerms, error } = await supabase
        .from('gps_user_permissions' as any)
        .select('*')
        .eq('company_id', companyId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching my GPS permissions:', error);
      }

      // Return existing permissions or defaults
      return (existingPerms || {
        id: '',
        company_id: companyId,
        user_id: user.id,
        can_view_live: true,
        can_view_history: true,
        history_days_limit: 30,
        can_send_commands: false,
        can_edit_devices: false,
        can_view_reports: true,
        can_share_tracking: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }) as GpsUserPermissions;
    },
    enabled: !!companyId,
  });

  // Update user permissions
  const updatePermissions = useMutation({
    mutationFn: async (updates: Partial<GpsUserPermissions> & { user_id: string }) => {
      // We'll use edge function for this complex operation
      const { data, error } = await supabase.functions.invoke('fleet-gps-user-permissions', {
        body: {
          company_id: companyId,
          ...updates,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gps-user-permissions'] });
      queryClient.invalidateQueries({ queryKey: ['gps-my-permissions'] });
      toast.success('Permissões atualizadas com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao atualizar permissões');
      console.error('Error updating permissions:', error);
    },
  });

  return {
    permissions,
    myPermissions,
    isLoading,
    updatePermissions,
  };
}
